import z from 'zod'

import { nonEmptyArraySchema } from '@mntn-dev/utilities'
import { EnumBuilder, type ZodInfer } from '@mntn-dev/utility-types'

import { ModelFilterSchema } from '../model-filter-schema.ts'
import { ProjectStatusEnum } from './project.models.ts'
import {
  DollarsSchema,
  NonEmptyStringSchema,
  PercentageSchema,
  TimestampSchema,
} from './property.models.ts'
import {
  AdvancePaymentIdSchema,
  AgreementIdSchema,
  InvoiceIdSchema,
  ProjectIdSchema,
  UserIdSchema,
} from './unique-id.models.ts'

/**
 * Project statuses that are eligible for advance payment requests
 */
export const AdvancePaymentRequestProjectStatuses = [
  ProjectStatusEnum.pre_production,
  ProjectStatusEnum.pre_production_complete,
  ProjectStatusEnum.production,
  ProjectStatusEnum.post_production,
] as const

export const [
  OpenAdvancePaymentStatuses,
  OpenAdvancePaymentStatusSchema,
  OpenAdvancePaymentStatusEnum,
] = EnumBuilder(
  'submitted', // The advance payment request has been submitted by the maker
  'accepted', // The advance payment request has been accepted by QFMP
  'approved' // The advance payment request has been approved by Finance
)

export type OpenAdvancePaymentStatus = ZodInfer<
  typeof OpenAdvancePaymentStatusSchema
>

export const [
  CompletedAdvancePaymentStatuses,
  CompletedAdvancePaymentStatusSchema,
  CompletedAdvancePaymentStatusEnum,
] = EnumBuilder(
  'canceled', // The advance payment request has been canceled
  'failed', // The advance payment request failed during processing (can be retried/canceled)
  'processed', // The advance payment request has been processed (paid, voided, refunded)
  'rejected' // The advance payment request has been rejected (by QFMP or Finance)
)

export type CompletedAdvancePaymentStatus = ZodInfer<
  typeof CompletedAdvancePaymentStatusSchema
>

/**
 * Advance Payment Status
 */
export const [
  AdvancePaymentStatuses,
  AdvancePaymentStatusSchema,
  AdvancePaymentStatusEnum,
] = EnumBuilder(
  ...OpenAdvancePaymentStatuses,
  ...CompletedAdvancePaymentStatuses
)

export type AdvancePaymentStatus = ZodInfer<typeof AdvancePaymentStatusSchema>

export const AdvancePaymentReviewWaiversSchema = z
  .object({
    feeExemption: z.boolean(),
    overLimitException: z.boolean(),
    multipleAdvancesException: z.boolean(),
  })
  .partial()

export type AdvancePaymentReviewWaivers = ZodInfer<
  typeof AdvancePaymentReviewWaiversSchema
>

/**
 * Advance Payment Domain Select Model
 */
export const AdvancePaymentDomainSelectModelSchema = z.object({
  advancePaymentId: AdvancePaymentIdSchema,
  projectId: ProjectIdSchema,
  status: AdvancePaymentStatusSchema,
  isStatusAlertDismissed: z.boolean(),
  request: z.object({
    actorId: UserIdSchema,
    timestamp: TimestampSchema,
    amount: DollarsSchema(),
    amountPercent: PercentageSchema,
    previousAdvancePaymentsAmount: DollarsSchema(),
    cumulativeAmountPercent: PercentageSchema,
    fee: DollarsSchema(),
    neededByDate: TimestampSchema,
    reason: NonEmptyStringSchema(),
    agreements: nonEmptyArraySchema(AgreementIdSchema),
  }),
  review: z
    .object({
      actorId: UserIdSchema,
      timestamp: TimestampSchema,
      message: NonEmptyStringSchema().optional(),
      waivers: AdvancePaymentReviewWaiversSchema,
    })
    .optional(),
  invoiceId: InvoiceIdSchema.optional(),
})

export type AdvancePaymentDomainSelectModel = ZodInfer<
  typeof AdvancePaymentDomainSelectModelSchema
>

/**
 * Advance Payment Domain Insert Model
 */
export const AdvancePaymentDomainInsertModelSchema =
  AdvancePaymentDomainSelectModelSchema.omit({
    status: true, // Created as 'submitted'
    isStatusAlertDismissed: true,
    review: true,
    invoiceId: true,
  })
    .extend({
      request: AdvancePaymentDomainSelectModelSchema.shape.request.omit({
        actorId: true,
        timestamp: true,
      }),
    })
    .partial({
      advancePaymentId: true,
    })

export type AdvancePaymentDomainInsertModel = ZodInfer<
  typeof AdvancePaymentDomainInsertModelSchema
>

/**
 * Advance Payment Domain Update Model
 */
export const AdvancePaymentDomainUpdateModelSchema =
  AdvancePaymentDomainSelectModelSchema.pick({
    status: true,
    isStatusAlertDismissed: true,
    invoiceId: true,
  })
    .extend({
      review: AdvancePaymentDomainSelectModelSchema.shape.review
        .unwrap()
        .omit({
          actorId: true,
          timestamp: true,
        })
        .partial(),
    })
    .partial()

export type AdvancePaymentDomainUpdateModel = ZodInfer<
  typeof AdvancePaymentDomainUpdateModelSchema
>

/**
 * Advance Payment Domain Select Model Filters
 */
export const AdvancePaymentDomainSelectModelFiltersSchema = z
  .object({
    advancePaymentId: ModelFilterSchema(AdvancePaymentIdSchema),
    projectId: ModelFilterSchema(ProjectIdSchema),
    status: ModelFilterSchema(AdvancePaymentStatusSchema),
    isStatusAlertDismissed: z.boolean(),
    invoiceId: ModelFilterSchema(InvoiceIdSchema),
  })
  .partial()

export type AdvancePaymentDomainSelectModelFilters = ZodInfer<
  typeof AdvancePaymentDomainSelectModelFiltersSchema
>
